import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Loader2, ArrowRight, Check, MessageCircle } from "lucide-react";
import { publicApi, publicError } from "@/lib/publicApi";
import { useSiteSettings, buildWhatsAppLink } from "@/lib/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import { ctaForServiceSlug } from "@/lib/ctas";
import AuditReassurance from "@/components/public/AuditReassurance";
import { PageHero, SectionTitle, CheckPill } from "@/components/public/Primitives";
import SEO from "@/components/public/SEO";
import { getLucideIcon } from "@/lib/iconMap";
import CaseStudyCard from "@/components/public/CaseStudyCard";
import FAQAccordion from "@/components/public/FAQAccordion";
import CTASection from "@/components/public/CTASection";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
    const { slug } = useParams();
    const { settings, hasWhatsApp } = useSiteSettings();
    const [service, setService] = useState(null);
    const [error, setError] = useState(null);
    const [related, setRelated] = useState([]);
    const [faqs, setFaqs] = useState([]);

    useEffect(() => {
        setService(null);
        setError(null);
        publicApi
            .get(`/services/${slug}`)
            .then((r) => setService(r.data))
            .catch((e) => setError(e?.response?.status === 404 ? "notfound" : publicError(e)));
        publicApi.get("/case-studies").then((r) => {
            const all = r.data || [];
            const match = all.filter((c) => (c.related_service_slugs || []).includes(slug));
            setRelated((match.length ? match : all).slice(0, 3));
        }).catch(() => {});
        publicApi.get("/faqs").then((r) => setFaqs((r.data || []).slice(0, 6))).catch(() => {});
    }, [slug]);

    if (error === "notfound") return <NotFound message="That service doesn't exist." />;
    if (!service) {
        return (
            <div className="min-h-[60vh] grid place-items-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const Icon = getLucideIcon(service.icon);
    const waMessage = `Hello ${settings.company_name || "Omega IT Ltd"}, I'm interested in your ${service.title} service. Can you please share more details?`;
    const waHref = hasWhatsApp ? buildWhatsAppLink(settings.whatsapp_number, waMessage) : null;
    const origin =
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
    const canonical = `${origin}/services/${service.slug}`;
    const serviceJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        description: service.short_description,
        provider: { "@type": "Organization", name: "Omega IT Ltd" },
        areaServed: "GB",
        serviceType: service.title,
        url: canonical,
    };

    return (
        <div data-testid={`service-detail-${service.slug}`}>
            <SEO
                title={service.seo_title || service.title}
                description={service.seo_description || service.short_description}
                canonical={canonical}
                ogImage={service.og_image || service.featured_image}
                jsonLd={serviceJsonLd}
            />
            <section className="bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-12 h-12 rounded-xl bg-slate-950 text-white grid place-items-center"><Icon className="w-5 h-5" /></span>
                            <div className="label-eyebrow">Service</div>
                        </div>
                        <h1 className="font-display font-black tracking-tight text-slate-950 text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">{service.title}</h1>
                        <p className="text-base sm:text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">{service.short_description}</p>
                        <div className="mt-9 flex flex-wrap gap-3 items-start">
                            <div>
                                <Button asChild size="lg" className="bg-slate-950 hover:bg-slate-800 text-white font-semibold" data-testid={`service-detail-cta-${service.slug}`}>
                                    <Link to={ctaForServiceSlug(service.slug).href}>{ctaForServiceSlug(service.slug).label}</Link>
                                </Button>
                                {ctaForServiceSlug(service.slug).key === "audit" && <AuditReassurance />}
                            </div>
                            {waHref && (
                                <Button asChild size="lg" variant="outline" className="border-emerald-500/30 text-emerald-700 hover:bg-emerald-50 font-semibold">
                                    <a href={waHref} target="_blank" rel="noreferrer noopener"><MessageCircle className="w-4 h-4 mr-1.5" />Chat on WhatsApp</a>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="px-5 sm:px-8 lg:px-12 py-20">
                <div className="max-w-4xl mx-auto">
                    <SectionTitle eyebrow="Overview" title="What this service includes." />
                    <div className="prose prose-slate max-w-none mt-8 text-slate-700">
                        <ReactMarkdown>{service.full_description || ""}</ReactMarkdown>
                    </div>
                </div>
            </section>

            {service.benefits?.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 pb-20">
                    <div className="max-w-7xl mx-auto">
                        <SectionTitle eyebrow="Benefits" title="What you get out of it." />
                        <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {service.benefits.map((b, i) => (<CheckPill key={i}>{b}</CheckPill>))}
                        </ul>
                    </div>
                </section>
            )}

            {service.features?.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 pb-20">
                    <div className="max-w-7xl mx-auto bg-slate-950 text-white rounded-3xl p-10 sm:p-14">
                        <SectionTitle eyebrow="What's inside" title={<span className="text-white">Features included.</span>} />
                        <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3.5">
                            {service.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200">
                                    <span className="w-5 h-5 rounded-full bg-white text-slate-950 grid place-items-center shrink-0 mt-0.5"><Check className="w-3 h-3" strokeWidth={3} /></span>
                                    <span className="leading-snug">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {service.process_steps?.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 pb-20">
                    <div className="max-w-5xl mx-auto">
                        <SectionTitle eyebrow="Process" title="How we work." />
                        <ol className="mt-10 space-y-5">
                            {service.process_steps.map((s, i) => (
                                <li key={i} className="flex gap-5 bg-white border border-slate-200 rounded-2xl p-6">
                                    <div className="w-10 h-10 rounded-lg bg-slate-950 text-white grid place-items-center font-display font-bold shrink-0">{i + 1}</div>
                                    <div>
                                        <h3 className="font-display text-lg font-bold text-slate-950">{s.title}</h3>
                                        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.description}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            {related.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 pb-20">
                    <div className="max-w-7xl mx-auto">
                        <SectionTitle eyebrow="Related work" title="Case studies you might like." />
                        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {related.map((c) => (<CaseStudyCard key={c.id} caseStudy={c} />))}
                        </div>
                    </div>
                </section>
            )}

            {faqs.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 pb-20">
                    <div className="max-w-3xl mx-auto">
                        <SectionTitle eyebrow="FAQ" title="Frequently asked questions." align="center" />
                        <div className="mt-10"><FAQAccordion faqs={faqs} /></div>
                    </div>
                </section>
            )}

            <CTASection />
        </div>
    );
}
